import type { ErrorReason } from '@lio/api-types';

/**
 * Extracts the user-friendly error message returned by the LIO Portal API.
 *
 * The API returns errors as JSON of the shape `{ error: { message: string } }`.
 * If the message is present, it is preferred over the UI fallback since the API
 * is the source of truth for user-facing error copy. If the API did not return
 * a message (network failure, non-JSON response, etc.), the provided fallback
 * is used instead.
 */
export const getApiErrorMessage = async (error: unknown, fallback: string): Promise<string> => {
  const body = await parseApiErrorBody(error);
  return body?.message ?? fallback;
};

/**
 * Extracts both the display message and the stable `reason` code from an API error response.
 * Use this instead of calling `getApiErrorMessage` when the caller also needs to branch on
 * `reason` — a real fetch `Response` body can only be read once, so a caller needing both
 * pieces must read them from a single `.json()` call rather than two separate ones.
 */
export const getApiError = async (
  error: unknown,
  fallback: string,
): Promise<{ message: string; reason?: ErrorReason }> => {
  const body = await parseApiErrorBody(error);
  return { message: body?.message ?? fallback, reason: body?.reason };
};

const parseApiErrorBody = async (error: unknown): Promise<{ message?: string; reason?: ErrorReason } | undefined> => {
  try {
    const response = (error as { response?: { json?: () => Promise<unknown> } })?.response;
    const data = (await response?.json?.()) as { error?: { message?: string; reason?: ErrorReason } } | undefined;
    return data?.error;
  } catch {
    return undefined;
  }
};
